import type { CSSProperties } from "react";

export type CardFormat = "story" | "post";

// ─── Brand-safe customization ────────────────────────────────────────────────

export interface TextStyle {
  fontFamily?: string;
  /** Size in px applied to the STORY format. Post format scales proportionally. */
  fontSizePx?: number;
  color?: string;
  italic?: boolean;
  underline?: boolean;
  /** Defaults true (brand ExtraBold). Set false for regular weight. */
  bold?: boolean;
  /** e.g. "0.4em" — only some fields use letter-spacing meaningfully */
  letterSpacing?: string;
  /** "left" | "center" — defaults to left */
  align?: "left" | "center";
}

export interface BackgroundSettings {
  /** Data URL or remote URL of the user-uploaded background image */
  imageUrl?: string;
  /** 0–1 (defaults to 0.3 when imageUrl is set). Sits between brand gradient and gold glow. */
  opacity?: number;
  /**
   * How the image maps onto the card box.
   * - cover   — fill the card, cropping whatever overflows (default).
   * - contain — show the whole image, letterboxed. Nothing is cut.
   */
  fit?: "cover" | "contain";
  /**
   * Which part of the image to keep when `fit: "cover"` crops. Portraits
   * usually want "top" so the subject's head survives the crop.
   */
  focal?: "top" | "center" | "bottom";
  /**
   * `fit: "contain"` only. Fills the letterbox gaps with a blurred,
   * darkened copy of the image instead of flat colour, so the photo reads
   * as part of the card rather than a rectangle pasted onto it.
   */
  blurBackdrop?: boolean;
}

/**
 * A bottom-anchored dark gradient that sits above the background image and
 * below the card content — the readability panel behind text.
 *
 * Deliberately separate from BackgroundSettings.opacity: opacity dims the
 * WHOLE photo (so making text legible washes out the entire image), while
 * the scrim darkens only the band the text occupies and leaves the rest of
 * the photo at full strength.
 */
export interface ScrimSettings {
  /** Peak darkness at the very bottom. 0 = none, 1 = solid black. */
  strength?: number;
  /** How far up the card the scrim reaches, as a fraction of card height. */
  height?: number;
}

export interface HeaderSettings {
  /** Override the "FansBall Knowledge" wordmark text. */
  wordmark?: string;
  /** Override the FBK logo image (data URL or remote URL). */
  logoUrl?: string;
  /** Override the right-side pill text. Default: "WORLD CUP 2026". */
  pillText?: string;
  /** Hide the header entirely (for full-bleed editorial covers). */
  hide?: boolean;
}

export interface FooterSettings {
  /** Hide the footer entirely (for full-bleed editorial covers). */
  hide?: boolean;
}

export interface CardStyleSettings {
  eyebrow?: TextStyle;
  headline?: TextStyle;
  goldHighlight?: TextStyle;
  /** Pull-quote / hook line — separate from body so it can have its own font + color */
  tagline?: TextStyle;
  body?: TextStyle;
  ctaText?: TextStyle;
  /** Used for large hero stat numbers (LFS survivors/eliminated, Leaderboard title number) */
  numbers?: TextStyle;
  /** Optional background image overlay (works for any card type) */
  background?: BackgroundSettings;
}

/** Curated, brand-friendly fonts. Loaded globally in index.html. */
export const FONT_OPTIONS: { label: string; value: string }[] = [
  { label: "Plus Jakarta Sans (default)", value: "'Plus Jakarta Sans', system-ui, sans-serif" },
  { label: "Inter", value: "'Inter', system-ui, sans-serif" },
  { label: "Bebas Neue (display)", value: "'Bebas Neue', sans-serif" },
  { label: "Anton (impact)", value: "'Anton', sans-serif" },
  { label: "Oswald (condensed)", value: "'Oswald', sans-serif" },
  { label: "Archivo Black (bold)", value: "'Archivo Black', sans-serif" },
];

/** Resolve a TextStyle override into concrete CSS for the given format. */
export function resolveTextStyle(
  override: TextStyle | undefined,
  defaults: {
    fontSizePx: number;
    color: string;
    letterSpacing?: string;
    italic?: boolean;
    underline?: boolean;
    bold?: boolean;
    fontFamily?: string;
    align?: "left" | "center";
  },
  /** Story uses fontSizePx as-is. Post downscales proportionally. */
  format: CardFormat,
): CSSProperties {
  const o = override ?? {};
  const sizePx =
    typeof o.fontSizePx === "number" ? o.fontSizePx : defaults.fontSizePx;
  const scaledSize = format === "post" ? sizePx * 0.78 : sizePx;
  const bold = o.bold ?? defaults.bold ?? true;
  const italic = o.italic ?? defaults.italic ?? false;
  const underline = o.underline ?? defaults.underline ?? false;
  return {
    fontFamily:
      o.fontFamily ||
      defaults.fontFamily ||
      "'Plus Jakarta Sans', system-ui, sans-serif",
    fontSize: scaledSize,
    color: o.color || defaults.color,
    fontWeight: bold ? 800 : 500,
    fontStyle: italic ? "italic" : "normal",
    textDecoration: underline ? "underline" : "none",
    letterSpacing: o.letterSpacing ?? defaults.letterSpacing,
    textAlign: o.align ?? defaults.align ?? "left",
  };
}

/** FBK brand palette + neutrals. */
export const BRAND_COLORS: { label: string; value: string }[] = [
  { label: "White", value: "#FFFFFF" },
  { label: "Gold", value: "#FFD700" },
  { label: "Primary green", value: "#0D8C3C" },
  { label: "Deep green", value: "#07321A" },
  { label: "Pitch green", value: "#166534" },
  { label: "Soft red", value: "#FF6B6B" },
  { label: "Off-black", value: "#0A2A1A" },
];


export type CardType =
  | "NEWS"
  | "ANNOUNCEMENT"
  | "LEADERBOARD"
  | "LFS_RESULT"
  | "DAILY_CHALLENGE"
  | "USER_STORY"
  | "MATCHDAY"
  | "TEAM_OF_WEEK"
  | "PARTNER_PITCH"
  | "LEAGUE_LAUNCH"
  | "FBK_LEAGUE"
  | "COUNTDOWN"
  | "MATCH_RESULT"
  | "MATCH_STORY"
  | "TOP_SCORERS"
  | "CAPTAIN_PICKS"
  | "FACTSHEET"
  | "BRACKET"
  | "CHAMPIONS"
  | "THANK_YOU";

export interface CardFormatSpec {
  width: number;
  height: number;
  padding: number;
  label: string;
}

export const CARD_FORMATS: Record<CardFormat, CardFormatSpec> = {
  story: { width: 1080, height: 1920, padding: 80, label: "Story" },
  post: { width: 1080, height: 1350, padding: 64, label: "Post" },
};

/** All FBK cards link to the same play URL. */
export const FBK_PLAY_URL = "fansballknowledge.com/play";

/** Per-template footer CTA verb. URL is always FBK_PLAY_URL. */
export const FOOTER_CTAS: Record<CardType, string> = {
  NEWS: "Read more",
  ANNOUNCEMENT: "Learn more",
  LEADERBOARD: "Climb the table",
  LFS_RESULT: "One pick to survive",
  DAILY_CHALLENGE: "Play the challenge",
  USER_STORY: "Read the full story",
  MATCHDAY: "Pick your team",
  TEAM_OF_WEEK: "Build your XI",
  PARTNER_PITCH: "Run your league with FBK",
  LEAGUE_LAUNCH: "Join the league",
  FBK_LEAGUE: "Join the official FBK League",
  COUNTDOWN: "Pre-game starts now",
  MATCH_RESULT: "Recap the match",
  MATCH_STORY: "Read the story",
  TOP_SCORERS: "Pick your XI",
  CAPTAIN_PICKS: "Pick your XI",
  FACTSHEET: "Read on FBK",
  BRACKET: "Predict the bracket",
  CHAMPIONS: "Celebrate on FBK",
  THANK_YOU: "See you next season",
};

/** Default footer-left text per card type. Admin can override via footerLeft. */
export function defaultFooterLeft(cardType: CardType): string {
  return `${FOOTER_CTAS[cardType]} · ${FBK_PLAY_URL}`;
}

export interface NewsCardContent {
  eyebrow: string;
  headline: string;
  goldHighlight?: string;
  body?: string;
  imageUrl?: string;
  ctaText?: string;
  /** Footer-left text. Empty/undefined falls back to defaultFooterLeft(cardType). */
  footerLeft?: string;
  hashtags?: string;
  header?: HeaderSettings;
  footer?: FooterSettings;
  style?: CardStyleSettings;
  /** Visual theme — defaults to "signature". See THEMES_PER_CARD_TYPE.NEWS. */
  theme?: CardTheme;
  /** Monochrome theme override color (defaults to brand green). */
  monoColor?: string;
}

export interface LeaderboardEntry {
  rank: number;
  /** Primary, large text — the user's team name */
  teamName: string;
  /** Secondary smaller text — user handle or display name */
  username: string;
  /** Optional ISO2 country code for the flag (e.g. "np", "br") */
  countryIso2?: string;
  points: number;
}

/**
 * What period the leaderboard covers. Drives the title lockup.
 *
 * - matchweek — "Matchweek 7". The per-round default.
 * - alltime   — "All Time". For the tournament-long standings once the
 *               competition is over and there's no next matchweek.
 * - custom    — free text, e.g. "Group Stage" / "Knockouts". Split across
 *               two fields so the second half renders in the accent colour,
 *               matching how the other two modes highlight their variable.
 */
export type LeaderboardScope = "matchweek" | "alltime" | "custom";

export interface LeaderboardCardContent {
  matchweek: number;
  /** Omitted = "matchweek", so existing saved cards render unchanged. */
  scope?: LeaderboardScope;
  /** Small tag above the title. Defaults to "LEADERBOARD". */
  eyebrow?: string;
  /** `custom` scope — the plain-coloured first half of the title. */
  customTitle?: string;
  /** `custom` scope — the accent-coloured second half of the title. */
  customTitleAccent?: string;
  entries: LeaderboardEntry[];
  footerLeft?: string;
  hashtags?: string;
  header?: HeaderSettings;
  footer?: FooterSettings;
  style?: CardStyleSettings;
  theme?: CardTheme;
  monoColor?: string;
}

export interface LfsNationPick {
  countryIso2: string;
  countryName: string;
  count: number;
}

export interface LfsCardContent {
  round: number;
  survivors: number;
  eliminated: number;
  topPicks: LfsNationPick[];
  footerLeft?: string;
  hashtags?: string;
  header?: HeaderSettings;
  footer?: FooterSettings;
  style?: CardStyleSettings;
  theme?: CardTheme;
  monoColor?: string;
}

export type DailyChallengeType =
  | "CAREER PATH"
  | "MYSTERY CLUB"
  | "HIGHER OR LOWER"
  | "WHO AM I"
  | "TIC TAC TOE"
  | "TRUE OR FALSE"
  | "MCQ";

export interface DailyChallengeCardContent {
  challengeType: DailyChallengeType;
  headline: string;
  goldHighlight?: string;
  subtext?: string;
  ctaText?: string;
  footerLeft?: string;
  hashtags?: string;
  header?: HeaderSettings;
  footer?: FooterSettings;
  style?: CardStyleSettings;
  theme?: CardTheme;
  monoColor?: string;
}

/**
 * A single chapter / page within a Player Story.
 * Page 0 is the cover; subsequent pages are chapters that tell the rest of the story.
 */
export interface StoryPage {
  eyebrow: string;
  /** Headline first part (e.g. "Lionel" or "THE DIAGNOSIS") */
  name: string;
  /** Headline gold part (e.g. "Messi." — usually only on the cover) */
  goldHighlight?: string;
  /** Pull-quote / hook line. Kept SHORT — feels intimate. */
  tagline?: string;
  /** Short body — aim for 2-3 sentences max per page. The drop-cap treatment hooks the reader. */
  body: string;
  /** Side portrait photo for this chapter */
  portraitImageUrl?: string;
  /** Per-chapter background image. Overrides the card-level Background tab image when set. */
  backgroundImageUrl?: string;
  /** Small stats strip (only useful on cover usually) */
  statsLine?: string;
  /** Per-page CTA — leave blank to hide. Typically only on the final chapter. */
  ctaText?: string;
}

export interface UserStoryCardContent {
  /** One or more pages. pages[0] is the cover; index 1+ are chapters. */
  pages: StoryPage[];
  footerLeft?: string;
  hashtags?: string;
  header?: HeaderSettings;
  footer?: FooterSettings;
  style?: CardStyleSettings;
  theme?: CardTheme;
  monoColor?: string;
}

/**
 * Shared theme IDs that any card template can opt into.
 * Each template whitelists which themes make sense for its content
 * via THEMES_PER_CARD_TYPE below.
 *
 * For Matchday only, see MatchdayTheme — it has additional themes that
 * only make sense for a head-to-head matchup layout.
 */
export type CardTheme =
  | "signature" // Brand green gradient + gold glow (default everywhere)
  | "spotlight" // Dark stadium-night with gold halo
  | "aurora" // Smooth multi-stop blend, atmospheric
  | "editorial" // Cream paper + Playfair serif — magazine cover
  | "halftone" // Two-tone with retro dot pattern
  | "monochrome" // Full-bleed single color with luminance-aware text
  | "collab" // Diagonal split: FBK green + partner color. Co-brand feel.
  | "knockout" // Deep emerald + subtle tournament-bracket pattern. Round-of-32 drama.
  | "trophy" // Black + massive bottom-up gold halo + corner sparkle. Champion gleam.
  | "anthem" // Vertical flag bands — emerald + gold ribbons. Ceremonial / anthem moment.
  | "emerald-noir"; // Black + tight emerald radial pulse + gold seam ring. Broadcast open.

/** Curated theme menu per template — keeps the picker focused on what fits. */
export const THEMES_PER_CARD_TYPE: Record<CardType, CardTheme[]> = {
  NEWS: ["signature", "spotlight", "editorial", "halftone", "monochrome", "knockout", "trophy", "emerald-noir"],
  ANNOUNCEMENT: ["signature", "spotlight", "aurora", "editorial", "monochrome", "knockout", "trophy", "anthem", "emerald-noir"],
  LEADERBOARD: ["signature", "spotlight", "monochrome"],
  LFS_RESULT: ["signature", "spotlight", "monochrome"],
  DAILY_CHALLENGE: ["signature", "spotlight", "aurora", "halftone", "monochrome"],
  USER_STORY: ["signature", "spotlight", "aurora", "editorial", "monochrome"],
  MATCHDAY: ["signature"], // Matchday uses MatchdayTheme — this row is unused but type-required
  TEAM_OF_WEEK: ["signature", "spotlight"],
  PARTNER_PITCH: ["collab", "signature", "spotlight", "monochrome", "editorial"],
  LEAGUE_LAUNCH: ["collab", "signature", "spotlight", "monochrome"],
  FBK_LEAGUE: ["spotlight", "signature", "aurora", "monochrome"],
  COUNTDOWN: ["signature"], // Countdown uses CountdownTheme — this row is unused but type-required
  MATCH_RESULT: ["signature"], // Match Result uses MatchResultTheme — this row is unused but type-required
  MATCH_STORY: ["signature", "spotlight", "editorial", "aurora", "monochrome"],
  TOP_SCORERS: ["signature", "spotlight", "monochrome"],
  CAPTAIN_PICKS: ["signature", "spotlight", "monochrome"],
  FACTSHEET: ["signature"], // Factsheet uses FactsheetVariant per slide — this row is unused but type-required
  BRACKET: ["signature"], // Bracket is single-themed (FBK majestic) — this row is unused but type-required
  CHAMPIONS: ["signature"], // Champions is nation-coloured per winner — this row is unused but type-required
  THANK_YOU: ["signature"], // Thank You uses ThankYouTheme — this row is unused but type-required
};

export type MatchdayTheme =
  | "signature" // Brand gradient + gold glow (default)
  | "split" // Two-tone home/away color split with gold seam
  | "spotlight" // Dark stadium-night gradient with central gold halo
  | "pitch" // Flat pitch-green with chalk-line tactical overlay
  | "monochrome" // Full-bleed single nation color with bold mono headline
  | "aurora" // Multi-stop conic blend of home + gold + away — atmospheric
  | "editorial" // Cream paper + Playfair serif headlines — magazine cover
  | "halftone" // Two-tone with retro halftone-dot pattern — Panini / pop-art
  | "stripes" // Diagonal home/away/gold stripes with motion — sporty
  | "rivalry" // Side-by-side flag halves + big player portraits + dramatic centre headline
  | "duel" // Cinematic dark base + dual spotlights + circular player portraits
  | "marquee"; // Fight-night / boxing-poster bold typography with diagonal nation blocks

/**
 * Eight bespoke visual themes for the MATCH_RESULT card. Each is a different
 * layout (not just a colour swap) tuned for post-match recap shares — picks
 * who's the "winner" and how dramatically the result lands.
 *
 * Winner-emphasis themes (VICTORY, CINEMA) auto-detect from the score and
 * brighten the winning side / fade the loser. The other six treat both teams
 * neutrally regardless of score.
 */
export type MatchResultTheme =
  | "victory"      // Winner's side flooded with their colour, loser fades to shadow
  | "stadium"      // Floodlit night stadium + LED-scoreboard score treatment
  | "newspaper"    // Cream paper + bold serif headline ("HOSTS STUN")
  | "half_half"    // Diagonal split filled with each team's colour, score on the seam
  | "neon"         // Synthwave grid floor + cyan/pink neon score
  | "minimal"      // Off-white background, ONE massive black score, whisper team labels
  | "cinema"       // Pure black + gold spotlight cone on the winner, loser in shadow
  | "ticker";      // Sky Sports / ESPN broadcast lower-third bar

/** A team in a match result. Code is ISO-2 country code for the flag lookup. */
export interface MatchResultTeam {
  /** Display name — e.g. "Mexico". */
  name: string;
  /** ISO-2 code — e.g. "mx". Drives the flagcdn lookup. */
  code: string;
  /** Optional uploaded flag image (data URL) — overrides flagcdn. */
  flagUrl?: string;
  /** Optional brand colour override. If absent, falls back to nationColors[code]. */
  color?: string;
}

/** One goalscorer entry shown in the optional goalscorers strip. */
export interface MatchResultGoal {
  /** Player surname or short name — e.g. "Lozano". */
  scorer: string;
  /** Minute string — e.g. "23'" or "67' (pen)". */
  minute: string;
  /** Which team scored — drives which side the goal sits on. */
  side: "home" | "away";
}

/**
 * Post-match recap card. Built for the moment immediately after full-time —
 * shareable, atmospheric, designed to be the FIRST thing fans see on their
 * feed when they open Insta after the match ends.
 */
export interface MatchResultCardContent {
  /** Top eyebrow strip — "MATCHWEEK 1 · GROUP A" / "ROUND OF 16" / etc. */
  eyebrow: string;
  home: MatchResultTeam;
  away: MatchResultTeam;
  /** Home score. Whole numbers only — penalty shootouts use the agg/penalty field. */
  homeScore: number;
  awayScore: number;
  /** Optional headline overlay — used heavily by NEWSPAPER theme. */
  headline?: string;
  /** Optional date label — "FRI · JUN 12". */
  dateLabel?: string;
  /** Optional venue — "Estadio Azteca". */
  venue?: string;
  /** Optional hot-take / one-line summary under the score. */
  tagline?: string;
  /** Optional goalscorer strip — rendered when at least one entry present. */
  goals?: MatchResultGoal[];
  /** Optional "Man of the Match" line — "MOTM: Lozano". */
  motm?: string;
  /** Optional aggregate / penalties annotation — "AET" / "Pens 4-2" / "Agg 3-2". */
  aggregateNote?: string;
  footerLeft?: string;
  hashtags?: string;
  header?: HeaderSettings;
  footer?: FooterSettings;
  style?: CardStyleSettings;
  /** Pick one of the eight bespoke MATCH_RESULT themes. */
  theme: MatchResultTheme;
  /** Optional FBK-side accent override (used by neutral themes when no team colour fits). */
  monoColor?: string;
}

/** Resolve the winner of a match for theme winner-emphasis logic. */
export function resolveMatchWinner(
  homeScore: number,
  awayScore: number,
): "home" | "away" | "draw" {
  if (homeScore > awayScore) return "home";
  if (awayScore > homeScore) return "away";
  return "draw";
}

export interface MatchdayNation {
  /** Display name e.g. "Portugal" */
  name: string;
  /** ISO2-style code from the Nations table e.g. "pt" */
  code: string;
  /** Flag image URL (falls back to flagcdn lookup on the card) */
  flagUrl?: string;
}

export interface MatchdayFeaturedPlayer {
  name: string;
  /** Photo upload (data URL) or remote URL */
  photoUrl?: string;
}

export interface MatchdayCardContent {
  /** "MATCHDAY 02 · GROUP F" — small uppercase eyebrow */
  eyebrow: string;
  home: MatchdayNation;
  away: MatchdayNation;
  /** Pre-formatted date label e.g. "15 JUN · SUN" */
  dateLabel: string;
  /** Local-clock kickoff label e.g. "21:00 LOCAL" */
  kickoffLabel: string;
  venue: string;
  /** City under the venue, optional */
  city?: string;
  /** Optional italic pull-quote / tagline */
  tagline?: string;
  /** Featured player per side (both optional) */
  featuredHome?: MatchdayFeaturedPlayer;
  featuredAway?: MatchdayFeaturedPlayer;
  /**
   * Optional narrative tag — a short historical context line shown as a
   * pill near the bottom on the rivalry/duel/marquee themes.
   * e.g. "2002: SENEGAL STUNNED FRANCE" or "FIRST MEETING SINCE 2014".
   * Ignored by the other themes.
   */
  narrativeTag?: string;
  /** Render style. See MatchdayTheme union for each variant's purpose. */
  theme: MatchdayTheme;
  /** Override colors for split / mono themes (defaults from NATION_COLORS) */
  homeColor?: string;
  awayColor?: string;
  /** Monochrome theme only — which side dominates the card. Defaults to "home". */
  monoSide?: "home" | "away";
  ctaText?: string;
  footerLeft?: string;
  hashtags?: string;
  header?: HeaderSettings;
  footer?: FooterSettings;
  style?: CardStyleSettings;
}

/** One player tile rendered onto the TEAM_OF_WEEK pitch. */
export interface TeamOfWeekPlayerTile {
  name: string;
  position: "GK" | "DEF" | "MID" | "FWD";
  /** Player photo — data URL or remote URL. Falls back to initials avatar. */
  photoUrl?: string | null;
  /** Club name (used as alt text + fallback when crest missing) */
  clubName?: string;
  clubLogoUrl?: string | null;
  /** ISO2-style nation code for flagcdn lookup, e.g. "br" */
  nationCode?: string;
  nationFlagUrl?: string | null;
  points: number;
  isCaptain?: boolean;
}

export interface TeamOfWeekCardContent {
  /** "Matchweek 03" — the gameweek name shown as title */
  gameweekName: string;
  /** Eyebrow above title. Defaults to "TEAM OF THE WEEK". */
  eyebrow?: string;
  /** "4-4-2" / "4-3-3" / etc. — string is open so any formation string is valid */
  formation: string;
  /** Total points across all 11 players (shown in footer-strip) */
  totalPoints: number;
  /** Exactly 11 tiles in render order: FWD row(s) → MID → DEF → GK. */
  players: TeamOfWeekPlayerTile[];
  /** Optional pull-quote shown above the pitch */
  tagline?: string;
  footerLeft?: string;
  hashtags?: string;
  header?: HeaderSettings;
  footer?: FooterSettings;
  style?: CardStyleSettings;
  theme?: CardTheme;
  monoColor?: string;
}

/**
 * One step in the 3-step "how it works" strip on the PARTNER_PITCH card.
 * Limit to 3 steps in practice — the layout reserves vertical room for that.
 */
export interface PartnerPitchStep {
  /** Short verb phrase: "We set it up in 10 min" */
  text: string;
}

/** One benefit checklist line on the PARTNER_PITCH card. */
export interface PartnerPitchBenefit {
  text: string;
}

/**
 * A chapter slide rendered AFTER the cover in a multi-page PARTNER_PITCH deck.
 * Cover (index 0) keeps the rich co-brand-bar layout from PartnerPitchCardContent;
 * chapters (index 1+) use a simpler info layout — eyebrow + headline + body — that
 * suits app walkthroughs, prize-format tables, what-you-do/what-we-do splits, etc.
 *
 * Body supports double-newline paragraphs and line-prefixed list rows:
 *   "→ Sign up free"
 *   "→ Pick your 11"
 * The renderer keeps the prefix verbatim so the admin controls the bullet style.
 */
export interface PitchExtraPage {
  eyebrow: string;
  /** Headline first part (e.g. "How a user plays FBK") */
  headline: string;
  /** Optional gold-accented second line under the headline. */
  goldHighlight?: string;
  /** Multi-line body — line-breaks render as visible breaks (for lists). */
  body: string;
  /** Optional photo / illustration shown on the right column. */
  imageUrl?: string;
  /** Per-chapter background image. Overrides the card-level Background tab. */
  backgroundImageUrl?: string;
  /** Optional CTA pill text. Typically only on the final chapter. */
  ctaText?: string;
}

/**
 * B2B outreach card sent to a business owner (café, shop, consultancy, IT
 * academy, etc.) to convince them to launch a private fantasy league for their
 * customers. Co-branded with the partner's logo + a "FBK ✕ Partner" mark.
 */
export interface PartnerPitchCardContent {
  /** "FBK FOR CAFÉS" / "FBK FOR CONSULTANCIES" — keep short. */
  eyebrow: string;
  /** "Run a Fantasy League" — first part of the headline. */
  headline: string;
  /** "for your regulars." — gold-accented closing line. Optional. */
  goldHighlight?: string;
  /** The partner's display name. Sits in the co-brand mark next to the FBK logo. */
  partnerName: string;
  /** Partner logo URL. Auto-embedded via ImageUploadField. */
  partnerLogoUrl?: string;
  /** Partner brand color — drives the right half of the 'collab' theme. */
  partnerColor?: string;
  /** Three short steps. Render shows numbered "① ② ③" prefixes. */
  steps: PartnerPitchStep[];
  /** Three benefit checklist lines. Render shows "✓" prefixes. */
  benefits: PartnerPitchBenefit[];
  /** Big CTA pill text. "Start your league" / "Let's talk" / etc. */
  ctaText?: string;
  /**
   * Optional chapter slides AFTER the cover. When empty, PartnerPitch behaves
   * as a single-page card (current behaviour). When populated, the admin can
   * paginate through cover + chapters and bulk-export them all as a zip.
   */
  extraPages?: PitchExtraPage[];
  footerLeft?: string;
  hashtags?: string;
  header?: HeaderSettings;
  footer?: FooterSettings;
  style?: CardStyleSettings;
  theme?: CardTheme;
  monoColor?: string;
}

/**
 * Customer-facing card a partner posts AFTER signing up — announces their live
 * private fantasy league. Partner logo is the hero; FBK is "powered by" small
 * print. Includes the prize block + join code + optional QR.
 */
export interface LeagueLaunchCardContent {
  /** "BLUE CAFÉ · MATCHWEEK 04" — keep short. */
  eyebrow: string;
  /** Big headline. Default "PLAY. PREDICT. WIN." */
  headline: string;
  /** Optional last line of the headline, rendered in gold accent.
   *  Use to land the closer on its own line (e.g. PLAY/WIN/SIP. with SIP. gold). */
  goldHighlight?: string;
  /** Optional smaller line under the headline. */
  tagline?: string;
  /** Partner's display name — used in the hero area + filename. */
  partnerName: string;
  /** Partner logo URL — rendered LARGE at the top as the hero. */
  partnerLogoUrl?: string;
  /** Partner brand color — drives the 'collab' theme accent. */
  partnerColor?: string;
  /** Headline of the prize block. Default "TOP 3 WIN". */
  prizeTitle: string;
  /** What they actually win. Free-text — multi-line allowed. */
  prizeText: string;
  /** 6-digit / short join code printed next to the QR. */
  joinCode?: string;
  /** Pre-generated QR code image (PNG). Admin uploads — we don't generate inline. */
  qrImageUrl?: string;
  /** "Free to join · Open only to [Partner] customers" — small footnote. */
  smallPrint?: string;
  footerLeft?: string;
  hashtags?: string;
  header?: HeaderSettings;
  footer?: FooterSettings;
  style?: CardStyleSettings;
  theme?: CardTheme;
  monoColor?: string;
}

/**
 * FBK's OWN league hype card — first-party, not co-branded. Used to drive
 * FBK fans on Instagram / TikTok to join the official league. Built for
 * scroll-stopping curiosity: stacked verb headline, an optional MYSTERY mode
 * on the prize block (covers the prize with "?? REVEALED [DATE] ??" until
 * the admin flips it), social-proof player count, and a big join code chip.
 */
export interface FbkLeagueCardContent {
  /** "FBK LEAGUE · MATCHWEEK 01" — short and dated. */
  eyebrow: string;
  /**
   * Big stacked headline. Use \n line-breaks for stacked words.
   * Default reads "PLAY.\nWIN.\nREPEAT." — each line one chant.
   */
  headline: string;
  /** Last word/line rendered in gold accent. Optional. */
  goldHighlight?: string;
  /** Small italic subheadline under the headline. Optional. */
  tagline?: string;
  /** Prize block title. Default "🏆 TOP 3 WIN". */
  prizeTitle: string;
  /**
   * The actual prize text — multi-line OK. Shown ONLY when mysteryMode is off.
   * When mysteryMode is on, this is hidden behind the reveal teaser.
   */
  prizeText: string;
  /**
   * Mystery toggle. When true, the prize block shows "?? REVEALED [date] ??"
   * + a teaser line instead of the prize text. Lets the admin run drip-reveal
   * campaigns: tease → reveal → next-week tease, all from one template.
   */
  mysteryMode?: boolean;
  /** Date string for the mystery reveal (e.g. "SUNDAY") — only shown when mysteryMode is on. */
  mysteryRevealAt?: string;
  /** Short teaser line under the mystery cover. e.g. "Something legendary." */
  mysteryTeaser?: string;
  /** Social-proof number — "🔥 12,438 PLAYERS ALREADY IN" — drives FOMO. */
  playerCountLabel?: string;
  /** Big bold join code shown in its own chip. */
  joinCode: string;
  /** Big CTA pill text. */
  ctaText?: string;
  /** Small footnote line under the CTA. */
  smallPrint?: string;
  footerLeft?: string;
  hashtags?: string;
  header?: HeaderSettings;
  footer?: FooterSettings;
  style?: CardStyleSettings;
  theme?: CardTheme;
  monoColor?: string;
}

/**
 * Five hand-tuned moods for the COUNTDOWN card. Each is a different visual
 * treatment — the admin picks one to match the energy of the day count.
 * Suggestions baked into the form:
 *   horizon  → 100+ days (calm, distant, star-flecked midnight)
 *   signature → 50–100 days (classic FBK green + gold)
 *   pulse    → 20–50 days (dark spotlight, intensifying gold halo)
 *   blaze    → 7–20 days (sunset orange + red, sunburst rays)
 *   ignite   → <7 days (peak hype, pure gold + motion lines)
 */
export type CountdownTheme =
  | "horizon"
  | "signature"
  | "pulse"
  | "blaze"
  | "ignite";

/**
 * "The world in 247 days" — a hype card with a mega day-count number as the
 * hero. Standalone visual system (5 themes, see CountdownTheme); the generic
 * CardTheme system isn't used here.
 */
export interface CountdownCardContent {
  /** Small uppercase tag above the number. Default "WORLD CUP IN". */
  eyebrow: string;
  /** The day count itself. Hero of the card. */
  days: number;
  /** Word under the number. Default "DAYS". Set to "DAY" for the 1-day card. */
  unitLabel: string;
  /** One-line subhead under the number. Optional. */
  subText?: string;
  /** Date pill near the bottom. e.g. "11 JUN 2026" or "KICKOFF · JUN 11". */
  dateLabel?: string;
  /**
   * Optional CTA — e.g. "Build your XI" — shown as a pill above the footer.
   * Leave blank to hide.
   */
  ctaText?: string;
  theme: CountdownTheme;
  footerLeft?: string;
  hashtags?: string;
  header?: HeaderSettings;
  footer?: FooterSettings;
  style?: CardStyleSettings;
}

/**
 * Multi-page narrative deck for one specific match. Three pages:
 *   0 · Cover   — flags, group/matchday, date, venue, hype eyebrow
 *   1 · Result  — scoreline + goalscorer strip
 *   2 · Story   — backstory paragraph with optional featured player chip
 * Auto-populated from a picked Match + its MatchEvent list. The Story page is
 * where the editorial angle lives (admin types or pastes; Phase 2 adds an AI
 * draft button). Cover and Result reuse MatchResultTeam / MatchResultGoal to
 * stay aligned with the single-page MATCH_RESULT card.
 */
export interface MatchStoryCardContent {
  /** Source match this story is built around. Optional — admin can hand-build. */
  matchId?: number;

  // === Cover page ===========================================================
  /** "GROUP F · MATCHDAY 02" — short uppercase eyebrow. */
  eyebrow: string;
  home: MatchResultTeam;
  away: MatchResultTeam;
  /** "15 JUN · SUN" — pre-formatted date string. */
  dateLabel?: string;
  /** "Estadio Azteca · Mexico City" — venue + optional city. */
  venue?: string;
  /** Optional pull-quote on the cover. Italic. */
  coverTagline?: string;

  // === Result page ==========================================================
  homeScore: number;
  awayScore: number;
  /** Goalscorers + minutes. Maps from MatchEvent[].filter(GOAL/OWN_GOAL). */
  goals?: MatchResultGoal[];
  /** "Mexico stun South Africa with last-minute Jiménez header" — Result-page hook. */
  resultHeadline?: string;
  /** Optional aggregate / pens note — "AET", "Pens 4-2". */
  aggregateNote?: string;

  // === Story page ===========================================================
  /** "THE STORY OF" / "THE COMEBACK" — small uppercase eyebrow above the headline. */
  storyEyebrow?: string;
  /** "Jiménez writes" — main headline first part. */
  storyHeadline: string;
  /** "the impossible." — gold-accented closing. Optional. */
  storyGoldHighlight?: string;
  /** The narrative paragraph — multi-line OK. ~80–160 words is the sweet spot. */
  storyBody: string;
  /** Featured player chip — protagonist of the story. */
  featuredPlayerName?: string;
  /** Photo URL — auto-embed via Wikipedia fetcher or upload. */
  featuredPlayerPhotoUrl?: string;
  /**
   * Wikipedia summary pre-fetched by the form. Stored so the admin can re-use
   * it without re-fetching, and Phase 2's AI draft button can pass it to Claude.
   */
  featuredPlayerWikiSummary?: string;

  // === Common ===============================================================
  footerLeft?: string;
  hashtags?: string;
  header?: HeaderSettings;
  footer?: FooterSettings;
  style?: CardStyleSettings;
  theme?: CardTheme;
  monoColor?: string;
}

export type AnyCardContent =
  | { type: "NEWS" | "ANNOUNCEMENT"; content: NewsCardContent }
  | { type: "LEADERBOARD"; content: LeaderboardCardContent }
  | { type: "LFS_RESULT"; content: LfsCardContent }
  | { type: "DAILY_CHALLENGE"; content: DailyChallengeCardContent }
  | { type: "USER_STORY"; content: UserStoryCardContent }
  | { type: "MATCHDAY"; content: MatchdayCardContent }
  | { type: "TEAM_OF_WEEK"; content: TeamOfWeekCardContent }
  | { type: "PARTNER_PITCH"; content: PartnerPitchCardContent }
  | { type: "LEAGUE_LAUNCH"; content: LeagueLaunchCardContent }
  | { type: "FBK_LEAGUE"; content: FbkLeagueCardContent }
  | { type: "COUNTDOWN"; content: CountdownCardContent }
  | { type: "MATCH_RESULT"; content: MatchResultCardContent }
  | { type: "MATCH_STORY"; content: MatchStoryCardContent }
  | { type: "TOP_SCORERS"; content: CaptainPicksCardContent }
  | { type: "CAPTAIN_PICKS"; content: CaptainPicksCardContent }
  | { type: "FACTSHEET"; content: FactsheetCardContent }
  | { type: "BRACKET"; content: BracketCardContent }
  | { type: "CHAMPIONS"; content: ChampionsCardContent }
  | { type: "THANK_YOU"; content: ThankYouCardContent };

/**
 * Four visual treatments for the community thank-you / season-wrap post.
 *
 * - gratitude — FBK signature green→gold with drifting confetti. Warm,
 *               on-brand, the safe default.
 * - rollcall  — real usernames tiled as background texture behind the
 *               headline. Turns the post into a "find your name" hunt,
 *               which is what actually drives shares.
 * - wrapped   — Spotify-Wrapped energy: oversized stat numbers ARE the
 *               hero, headline plays support. Bold colour blocking.
 * - curtain   — cinematic black with a single spotlight cone. Restrained,
 *               end-credits feel. Best when the copy is the point.
 */
export type ThankYouTheme = "gratitude" | "rollcall" | "wrapped" | "curtain";

/** One community stat tile. Keep values pre-formatted ("12,480", "1.2M"). */
export interface ThankYouStat {
  value: string;
  label: string;
}

export interface ThankYouCardContent {
  theme: ThankYouTheme;
  /** "WORLD CUP 2026 · THAT'S A WRAP" */
  eyebrow: string;
  /** The hero line. "THANK YOU" by default. */
  headline: string;
  /** Sits under the headline — the warm bit. */
  subheadline?: string;
  /** Longer body copy. Keep to ~2 sentences; this is a poster. */
  message?: string;
  /**
   * Up to 4 stat tiles. The whole reason this template beats a plain
   * thank-you graphic: specificity. Seed these from the dashboard +
   * fantasy-leaderboard endpoints rather than typing them.
   */
  stats: ThankYouStat[];
  /**
   * Usernames rendered as background texture on the `rollcall` theme.
   * Ignored by the other three. Seeded from the admin users endpoint —
   * the more names the denser (and better) the texture reads.
   */
  names: string[];
  /** Personal sign-off. "— Sandeep & the FBK team" */
  signature?: string;
  heroImageUrl?: string;
  heroImageOpacity?: number;
  footerLeft?: string;
  hashtags?: string;
  header?: HeaderSettings;
  footer?: FooterSettings;
  style?: CardStyleSettings;
}

/**
 * Two layouts for the Champions card.
 *
 * - glory    — nation-coloured gradient stage, giant CHAMPIONS lockup,
 *              trophy emblem, star row. Works with zero photos, so it can
 *              be published the second the final whistle blows.
 * - portrait — full-bleed celebration photo (lifting the trophy, squad
 *              huddle) with the CHAMPIONS lockup in the lower third.
 *              Use once the real photos land.
 */
export type ChampionsVariant = "glory" | "portrait";

/**
 * A finalist's brand kit. Pre-seeding these lets the admin flip the winner
 * with one click instead of hand-entering six colour hexes under time
 * pressure while the trophy lift is happening.
 *
 * `titlesAfterWin` = how many World Cups that nation would hold if they
 * win the tournament this card is celebrating. Drives both the star row
 * and the "Nth WORLD TITLE" line.
 */
export interface ChampionsNationPreset {
  name: string;
  code: string;
  /** Base nation colour — drives the stage gradient. */
  primary: string;
  /** Secondary nation colour — used for the lower band + accents. */
  secondary: string;
  /** Highlight colour for the CHAMPIONS wordmark + stars. */
  accent: string;
  titlesAfterWin: number;
  /** Human label for the title-count line, e.g. "4TH WORLD TITLE". */
  titleLabel: string;
}

/**
 * The 2026 finalists, ready to swap between. Add more entries here if a
 * future final needs them — the form renders one button per preset.
 */
export const CHAMPIONS_NATION_PRESETS: ChampionsNationPreset[] = [
  {
    name: "Argentina",
    code: "ARG",
    primary: "#75AADB", // Albiceleste sky blue
    secondary: "#FFFFFF",
    accent: "#FFD700", // Sol de Mayo gold
    // 1978, 1986, 2022 → a 2026 win makes four.
    titlesAfterWin: 4,
    titleLabel: "4TH WORLD TITLE",
  },
  {
    name: "Spain",
    code: "ESP",
    primary: "#C60B1E", // Rojigualda red
    secondary: "#FFC400", // Spanish flag yellow
    accent: "#FFD700",
    // 2010 → a 2026 win makes two.
    titlesAfterWin: 2,
    titleLabel: "2ND WORLD TITLE",
  },
];

export interface ChampionsCardContent {
  variant: ChampionsVariant;
  /** Winning nation — everything below is driven off this. */
  nationName: string;
  nationCode: string;
  flagUrl?: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  /** "FIFA WORLD CUP 2026 · FINAL" */
  eyebrow: string;
  /** The hero word. "CHAMPIONS" by default; "CAMPEONES" also works. */
  championLabel: string;
  /** "4TH WORLD TITLE" — rendered under the star row. */
  titleCountLabel?: string;
  /** How many gold stars to render. Usually === titles held after the win. */
  starCount?: number;
  /** "ARGENTINA 2 — 1 SPAIN" — fill in the moment the game ends. */
  scoreLine?: string;
  /** Congratulations copy. Keep it short — this is a poster, not an article. */
  message?: string;
  /**
   * Trophy / celebration image. On `glory` this sits centred above the
   * wordmark; on `portrait` it becomes the full-bleed background.
   */
  heroImageUrl?: string;
  heroImageOpacity?: number;
  /** "19 JULY 2026 · METLIFE STADIUM" */
  dateLine?: string;
  footerLeft?: string;
  hashtags?: string;
  header?: HeaderSettings;
  footer?: FooterSettings;
  style?: CardStyleSettings;
}

/**
 * Five knockout-stage rounds the bracket card supports. Each maps directly
 * to a backend Phase enum value (one-to-one — no aliasing). Number of
 * matchups halves each round.
 */
export type BracketRound =
  | "ROUND_OF_32" // 16 matchups (WC 2026 expanded 48-team format)
  | "ROUND_OF_16" // 8 matchups
  | "QUARTER_FINAL" // 4 matchups
  | "SEMI_FINAL" // 2 matchups
  | "FINAL"; // 1 matchup

export const BRACKET_ROUND_LABEL: Record<BracketRound, string> = {
  ROUND_OF_32: "Round of 32",
  ROUND_OF_16: "Round of 16",
  QUARTER_FINAL: "Quarter Finals",
  SEMI_FINAL: "Semi Finals",
  FINAL: "Final",
};

export const BRACKET_ROUND_MATCH_COUNT: Record<BracketRound, number> = {
  ROUND_OF_32: 16,
  ROUND_OF_16: 8,
  QUARTER_FINAL: 4,
  SEMI_FINAL: 2,
  FINAL: 1,
};

/**
 * One matchup row in the bracket. Both sides are required to render the
 * row; an empty side renders a "TBD" placeholder so the card stays
 * complete during the seeding workflow.
 */
export interface BracketMatchup {
  homeName: string;
  homeFlagUrl?: string;
  awayName: string;
  awayFlagUrl?: string;
}

export interface BracketCardContent {
  /** Round the bracket displays. Drives row count + auto-seed query. */
  round: BracketRound;
  /** Big title shown above the bracket. Defaults to "KO STAGE". */
  title: string;
  /** Sub-title under the big title. Defaults to "WC 2026". */
  subtitle: string;
  /**
   * Center image between the two columns. Defaults to the FBK shield
   * (/logo.png) — admin can drop in a trophy/badge/photo override.
   */
  centerImageUrl?: string;
  /**
   * Matchups, ordered top-to-bottom across both columns.
   * Length must equal BRACKET_ROUND_MATCH_COUNT[round]. The card
   * splits them evenly: first half → left column, second half →
   * right column. For odd rounds (Final = 1), the matchup centers.
   */
  matchups: BracketMatchup[];
  footerLeft?: string;
  hashtags?: string;
  header?: HeaderSettings;
  footer?: FooterSettings;
  style?: CardStyleSettings;
}

/**
 * Three layout variants per Factsheet slide. Admin picks per slide so a
 * 9-slide carousel can mix shapes for visual rhythm.
 *
 * - cinematic   — full-bleed photo, centered headline, accent word.
 *                 Closest to the "FIFA has officially confirmed" reference.
 * - newsroom    — photo top 60%, headline + body in a bottom typography
 *                 panel. More editorial / broadcaster lower-third feel.
 * - pill        — photo full-bleed, small "RULE 02 / 09" gold pill above
 *                 the headline. Best for numbered series.
 */
export type FactsheetVariant =
  | "cinematic"
  | "newsroom"
  | "pill"
  | "memorial" // Reverent tribute: b/w-friendly, serif headline, no CTA/hashtags.
  | "split"; // Image band on top, text below on solid ground. Zero overlap.

/** One Factsheet slide — a single fact/rule rendered over a hero photo. */
export interface FactsheetSlide {
  /** Variant for this slide. Each can be different. */
  variant: FactsheetVariant;
  /**
   * Hero photo URL (data URL or remote). Fills the background.
   * Auto-darkened with an overlay so the typography reads.
   */
  imageUrl?: string;
  /** Small uppercase tag above the headline. Optional. */
  eyebrow?: string;
  /**
   * Main headline. Multi-line OK — use \n for explicit line breaks.
   * Renders in massive Bebas Neue / Anton condensed slab type.
   */
  headline: string;
  /**
   * One word (or short phrase) inside `headline` that should be
   * highlighted in the accent colour. If absent, the whole headline
   * stays white. The renderer wraps the FIRST occurrence of this
   * substring in the accent colour.
   */
  accentWord?: string;
  /** Optional short body paragraph under the headline. ~10-25 words. */
  body?: string;
  /** "Pill" variant only — the small pill text, e.g. "RULE 02 / 09". */
  pillLabel?: string;
  /** Accent colour for this slide. Defaults to FBK gold. */
  accentColor?: string;
  /**
   * How visible the hero photo is. 0 = fully dark (photo hidden by base),
   * 1 = fully bright (raw photo). Defaults to 0.85 — mostly visible with
   * enough darken-through so the headline still reads.
   */
  imageOpacity?: number;
  /** See BackgroundSettings.fit. Defaults to "cover". */
  imageFit?: "cover" | "contain";
  /** See BackgroundSettings.focal. Defaults to "center". */
  imageFocal?: "top" | "center" | "bottom";
  /**
   * Darkness of the readability panel behind the text, 0–1. Omitted falls
   * back to the variant's tuned default (see SCRIM_DEFAULTS in
   * FactsheetCard) rather than a single global value, because a newsroom
   * panel legitimately needs to be far darker than a cinematic one.
   */
  scrimStrength?: number;
  /** How far up the card the scrim reaches, 0–1. Variant-defaulted. */
  scrimHeight?: number;
  /**
   * Split variant only — what fraction of the card the image band occupies
   * before the text block starts. Defaults to 0.58.
   */
  splitRatio?: number;
  /**
   * Memorial variant only — a small caps line rendered under the name,
   * usually a date range ("1996 — 2025") or memorial date ("3 JULY 2025").
   * Ignored by other variants.
   */
  datesLine?: string;
}

/**
 * Multi-slide deck for explaining a set of facts / rules / changes.
 * Cover slide + N chapter slides — same pattern as PartnerPitch. Each
 * slide is a hero photo + bold typography overlay, FBK-themed.
 */
export interface FactsheetCardContent {
  /** Cover slide — always present. Page 0 in the deck. */
  cover: FactsheetSlide;
  /** Chapter slides — page 1+. Admin adds as many as the carousel needs. */
  extraSlides: FactsheetSlide[];
  footerLeft?: string;
  hashtags?: string;
  header?: HeaderSettings;
  footer?: FooterSettings;
  style?: CardStyleSettings;
}

/**
 * One row on the CAPTAIN_PICKS leaderboard — a player the crowd most often
 * gave the armband to in a given gameweek. The pickValue is a free-form
 * number (count or percent), displayed alongside the unit label.
 */
export interface CaptainPick {
  /** Display name (typically surname + initial, e.g. "Salah" or "Salah M.") */
  name: string;
  /** Optional photo (data URL or remote — auto-embedded by the form). */
  photoUrl?: string | null;
  /**
   * Subtitle shown under the name. Carries the club name for top-scorers
   * cards, or the nation name when the data source is Top Captains (which
   * has no club info). Free text — admin can edit per row.
   */
  clubName?: string;
  /** Optional club crest. Auto-embedded if remote. */
  clubLogoUrl?: string | null;
  /** ISO2 nation code (e.g. "br") — drives flagcdn fallback. */
  nationCode?: string;
  nationFlagUrl?: string | null;
  /** Position badge — GK/DEF/MID/FWD. Rendered as a coloured pill on the right. */
  position?: "GK" | "DEF" | "MID" | "FWD";
  /** The pick number — either a raw count (e.g. 1240) or a percentage (e.g. 23). */
  pickValue: number;
}

/**
 * "Most captained" leaderboard for a gameweek. Hero of the card is a
 * ranked list (typically 10 rows) of the players the most squads
 * armbanded. Editable end-to-end — admin can hand-pick rows or seed
 * from a gameweek's eligible-players list.
 */
export interface CaptainPicksCardContent {
  /** "MOST CAPTAINED · MATCHWEEK 3" — short uppercase eyebrow. */
  eyebrow: string;
  /** Main headline part. e.g. "Captain Picks". */
  headline: string;
  /** Gold-accented closing — typically "Top 10" or "This Week". */
  goldHighlight?: string;
  /** Optional pull-quote / hook line. */
  tagline?: string;
  /** Ranked list. Admin can show 3–15 rows; 10 is the natural default. */
  picks: CaptainPick[];
  /**
   * How to render the `pickValue` numbers:
   *   "percent" → "23%"   (good for "% of squads")
   *   "count"   → "1,240" (good for raw squad counts)
   */
  displayMode: "percent" | "count";
  /** Optional footnote shown under the list. e.g. "Across 14,372 squads · MW3". */
  totalLabel?: string;
  /**
   * Show the gold "C" armband sticker on each player photo. Defaults to
   * true (this is the captain-picks card). Set false for top-scorers
   * variants where the armband would be misleading.
   */
  showCaptainArmband?: boolean;
  footerLeft?: string;
  hashtags?: string;
  header?: HeaderSettings;
  footer?: FooterSettings;
  style?: CardStyleSettings;
  theme?: CardTheme;
  monoColor?: string;
}
